import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import Input from '../../form/Input';
import Select from '../../form/Select';
import { useProductStore } from '../../../store/useProduct';
import { useOrderStore } from '../../../store/useOrder';
import { useFranchiseStore } from '../../../store/useFranchise';
import { validationOrderRegistration } from '../../../util/validation';
import { IOrderRegistration, IOrderFormData } from '../../../type/type';

/**
 * @author ellie
 * @date 2026.02.25
 * @description 단건 주문 등록 페이지
 */
const OrderSingle = () => {
  const navigate = useNavigate();
  const { products, getProducts } = useProductStore();
  const { postOrder } = useOrderStore();
  const { myfranchise, getMyFranchise } = useFranchiseStore();

  // 비즈 프로필 활성화 여부
  const isBizProfileEnabled = useMemo(
    () => Boolean(myfranchise?.bizmProfileKey),
    [myfranchise?.bizmProfileKey]
  );

  // 상품 옵션 목록
  const productOptions = useMemo(() => {
    if (!products) return [{ value: '', option: '상품을 선택하세요' }];
    return [
      { value: '', option: '상품을 선택하세요' },
      ...products.map(p => ({
        value: p.productCode,
        option: p.productName,
      })),
    ];
  }, [products]);

  // 선택된 상품 정보
  const [selectedProduct, setSelectedProduct] = useState<{
    productCode: string;
    productName: string;
    custInfoCount: number;
  } | null>(null);

  // 폼 상태
  const [formData, setFormData] = useState<IOrderFormData>({
    sequence: '1',
    productCodeOrName: '',
    email: '',
    telNo: '',
    name1: '',
    solarType1: '양력',
    year1: '',
    month1: '',
    day1: '',
    hour1: '',
    minute1: '',
    sex1: '',
    name2: '',
    solarType2: '',
    year2: '',
    month2: '',
    day2: '',
    hour2: '',
    minute2: '',
    sex2: '',
  });

  // 시간모름 체크박스 상태
  const [unknownTime1, setUnknownTime1] = useState(false);
  const [unknownTime2, setUnknownTime2] = useState(false);

  // 에러 상태
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 로딩 상태
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getProducts();
    getMyFranchise();
  }, [getProducts, getMyFranchise]);

  // 상품 선택 시 custInfoCount 확인
  useEffect(() => {
    if (formData.productCodeOrName && products) {
      const product = products.find(
        p => p.productCode === formData.productCodeOrName || p.productName === formData.productCodeOrName
      );
      setSelectedProduct(product ? {
        productCode: product.productCode,
        productName: product.productName,
        custInfoCount: product.custInfoCount,
      } : null);
    } else {
      setSelectedProduct(null);
    }
  }, [formData.productCodeOrName, products]);

  // 폼 값 변경 핸들러
  const handleChange = useCallback((field: keyof IOrderFormData, value: string) => {
    // 전화번호 포맷팅
    if (field === 'telNo' && value) {
      value = value.replace(/\D/g, '');
      if (value.length > 11) {
        value = value.slice(0, 11);
      }
      if (value.length > 3 && value.length <= 7) {
        value = `${value.slice(0, 3)}-${value.slice(3)}`;
      } else if (value.length > 7) {
        value = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`;
      }
    }

    // 년도: 숫자만, 4자리까지
    if ((field === 'year1' || field === 'year2') && value) {
      value = value.replace(/\D/g, '').slice(0, 4);
    }

    // 월: 숫자만, 1-12
    if ((field === 'month1' || field === 'month2') && value) {
      value = value.replace(/\D/g, '').slice(0, 2);
      const num = parseInt(value, 10);
      if (num > 12) value = '12';
    }

    // 일: 숫자만, 1-31
    if ((field === 'day1' || field === 'day2') && value) {
      value = value.replace(/\D/g, '').slice(0, 2);
      const num = parseInt(value, 10);
      if (num > 31) value = '31';
    }

    // 시: 숫자만, 0-23
    if ((field === 'hour1' || field === 'hour2') && value) {
      value = value.replace(/\D/g, '').slice(0, 2);
      const num = parseInt(value, 10);
      if (num > 23) value = '23';
    }

    // 분: 숫자만, 0-59
    if ((field === 'minute1' || field === 'minute2') && value) {
      value = value.replace(/\D/g, '').slice(0, 2);
      const num = parseInt(value, 10);
      if (num > 59) value = '59';
    }

    // 이름: 10자까지
    if ((field === 'name1' || field === 'name2') && value) {
      value = value.slice(0, 10);
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    // 해당 필드 에러 클리어
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  // 유효성 검사
  const validate = useCallback(() => {
    if (!products) return false;

    const productData = products.map(product => ({
      productCode: product.productCode,
      productName: product.productName,
      custInfoCount: product.custInfoCount,
    }));

    const validationResult = validationOrderRegistration([formData as unknown as IOrderRegistration], productData);
    
    console.log('=== Validation Debug ===');
    console.log('formData:', formData);
    console.log('productData:', productData);
    console.log('validationResult:', validationResult);
    
    // 기본 에러 가져오기
    const rowErrors = validationResult?.[0] || {};

    // 시간모름 체크 안 했는데 시간이 빈값인 경우 에러 추가
    if (!unknownTime1 && (!formData.hour1 || !formData.minute1)) {
      rowErrors.hour1 = '시간을 입력하거나 시간모름을 체크해주세요';
    }

    // 고객 정보 2가 있을 때
    if (selectedProduct?.custInfoCount === 2) {
      if (!unknownTime2 && (!formData.hour2 || !formData.minute2)) {
        rowErrors.hour2 = '시간을 입력하거나 시간모름을 체크해주세요';
      }
    }

    if (Object.keys(rowErrors).length > 0) {
      setErrors(rowErrors);
      return false;
    }

    setErrors({});
    return true;
  }, [formData, products, unknownTime1, unknownTime2, selectedProduct]);

  // 제출 핸들러
  const handleSubmit = useCallback(async (realtimeYn: 'Y' | 'N') => {
    if (!validate()) {
      toast.error('입력 내용을 확인해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const tempObj: Record<string, string> = {
        '양력': 'SOLAR',
        '음력': 'LUNAR',
        '음력(윤달)': 'LUNAR_INTER',
        '남성': 'M',
        '여성': 'F',
      };

      const submitData: IOrderRegistration = {
        sequence: Number(formData.sequence),
        productCodeOrName: formData.productCodeOrName,
        telNo: formData.telNo,
        email: formData.email as `${string}@${string}.${string}`,
        name1: formData.name1,
        solarType1: tempObj[formData.solarType1] as '양력' | '음력',
        year1: Number(formData.year1) as IOrderRegistration['year1'],
        month1: Number(formData.month1) as IOrderRegistration['month1'],
        day1: Number(formData.day1) as IOrderRegistration['day1'],
        hour1: formData.hour1 ? Number(formData.hour1) as IOrderRegistration['hour1'] : undefined,
        minute1: formData.minute1 ? Number(formData.minute1) as IOrderRegistration['minute1'] : undefined,
        sex1: tempObj[formData.sex1] as '남성' | '여성',
        realtimeYn,
        ...(selectedProduct?.custInfoCount === 2 && {
          name2: formData.name2,
          solarType2: tempObj[formData.solarType2] as '양력' | '음력',
          year2: Number(formData.year2) as IOrderRegistration['year2'],
          month2: Number(formData.month2) as IOrderRegistration['month2'],
          day2: Number(formData.day2) as IOrderRegistration['day2'],
          hour2: formData.hour2 ? Number(formData.hour2) as IOrderRegistration['hour2'] : undefined,
          minute2: formData.minute2 ? Number(formData.minute2) as IOrderRegistration['minute2'] : undefined,
          sex2: tempObj[formData.sex2] as '남성' | '여성',
        }),
      };

      await postOrder([submitData], navigate);
    } catch (error) {
      console.error('Order submit error:', error);
      toast.error('주문 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validate, postOrder, navigate]);

  // 폼 초기화
  const handleReset = useCallback(() => {
    setFormData({
      sequence: '1',
      productCodeOrName: '',
      email: '',
      telNo: '',
      name1: '',
      solarType1: '양력',
      year1: '',
      month1: '',
      day1: '',
      hour1: '',
      minute1: '',
      sex1: '',
      name2: '',
      solarType2: '',
      year2: '',
      month2: '',
      day2: '',
      hour2: '',
      minute2: '',
      sex2: '',
    });
    setErrors({});
    setSelectedProduct(null);
    setUnknownTime1(false);
    setUnknownTime2(false);
  }, []);

  // 공통 입력 필드 스타일
  const fieldClass = "!mb-4";
  const labelClass = "!block !text-sm !font-medium !text-gray-700 !mb-1";
  const errorClass = "!text-xs !text-red-500 !mt-1";

  return (
    <div className="!w-full !h-full !overflow-auto">
      <div className="!max-w-5xl !p-6">
        {/* 주문 정보 */}
        <div className="!bg-white !rounded-lg !shadow-sm !p-6 !mb-6">
          <h2 className="!text-lg !font-semibold !text-gray-800 !mb-4 !pb-2 !border-b">주문 정보</h2>
          
          <div className="!grid !grid-cols-1 md:!grid-cols-3 !gap-4">
            {/* 상품 선택 */}
            <div className={fieldClass}>
              <label className={labelClass}>상품 <span className="!text-red-500">*</span></label>
              <Select
                options={productOptions}
                value={formData.productCodeOrName}
                onChange={(e) => handleChange('productCodeOrName', e.target.value)}
                className="!w-full"
              />
              {errors.productCodeOrName && <p className={errorClass}>{errors.productCodeOrName}</p>}
            </div>

            {/* 연락처 - 비즈 프로필 연동 시에만 입력 가능 */}
            {isBizProfileEnabled && (
              <div className={fieldClass}>
                <label className={labelClass}>연락처 <span className="!text-gray-400 !text-xs">(선택)</span></label>
                <Input
                  textAlign="left"
                  value={formData.telNo}
                  onChange={(e) => handleChange('telNo', e.target.value)}
                  placeholder="010-1234-5678"
                  width="100%"
                />
                {errors.telNo && <p className={errorClass}>{errors.telNo}</p>}
              </div>
            )}

            {/* 이메일 */}
            <div className={fieldClass}>
              <label className={labelClass}>이메일 {!isBizProfileEnabled && <span className="!text-red-500">*</span>}</label>
              <Input
                textAlign="left"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="example@email.com"
                type="email"
                width="100%"
              />
              {errors.email && <p className={errorClass}>{errors.email}</p>}
            </div>
          </div>
        </div>

        {/* 고객 정보 1 */}
        <div className="!bg-white !rounded-lg !shadow-sm !p-6 !mb-6">
          <h2 className="!text-lg !font-semibold !text-gray-800 !mb-4 !pb-2 !border-b">
            고객 정보 1 <span className="!text-red-500">*</span>
          </h2>
          
          {/* 1행: 이름, 성별, 양/음력 */}
          <div className="!grid !grid-cols-1 sm:!grid-cols-3 !gap-4 !mb-4">
            <div>
              <label className={labelClass}>이름 <span className="!text-red-500">*</span></label>
              <Input
                textAlign="left"
                value={formData.name1}
                onChange={(e) => handleChange('name1', e.target.value)}
                placeholder="홍길동"
                width="100%"
              />
              {errors.name1 && <p className={errorClass}>{errors.name1}</p>}
            </div>
            <div>
              <label className={labelClass}>성별 <span className="!text-red-500">*</span></label>
              <Select
                options={[
                  { value: '', option: '선택하세요' },
                  { value: '남성', option: '남성' },
                  { value: '여성', option: '여성' },
                ]}
                value={formData.sex1}
                onChange={(e) => handleChange('sex1', e.target.value)}
                className="!w-full"
              />
              {errors.sex1 && <p className={errorClass}>{errors.sex1}</p>}
            </div>
            <div>
              <label className={labelClass}>양/음력 <span className="!text-red-500">*</span></label>
              <Select
                options={[
                  { value: '양력', option: '양력' },
                  { value: '음력', option: '음력' },
                  { value: '음력(윤달)', option: '음력(윤달)' },
                ]}
                value={formData.solarType1}
                onChange={(e) => handleChange('solarType1', e.target.value)}
                className="!w-full"
              />
              {errors.solarType1 && <p className={errorClass}>{errors.solarType1}</p>}
            </div>
          </div>

          {/* 2행: 생년월일 */}
          <div className="!mb-4">
            <label className={labelClass}>생년월일 <span className="!text-red-500">*</span></label>
            <div className="!flex !flex-wrap !gap-2 !items-center">
              <div className="!flex !items-center !gap-1">
                <Input
                  textAlign="left"
                  value={formData.year1}
                  onChange={(e) => handleChange('year1', e.target.value)}
                  placeholder="1990"
                  type="number"
                  width={80}
                />
                <span className="!text-gray-500">년</span>
              </div>
              <div className="!flex !items-center !gap-1">
                <Input
                  textAlign="left"
                  value={formData.month1}
                  onChange={(e) => handleChange('month1', e.target.value)}
                  placeholder="1"
                  type="number"
                  width={60}
                />
                <span className="!text-gray-500">월</span>
              </div>
              <div className="!flex !items-center !gap-1">
                <Input
                  textAlign="left"
                  value={formData.day1}
                  onChange={(e) => handleChange('day1', e.target.value)}
                  placeholder="15"
                  type="number"
                  width={60}
                />
                <span className="!text-gray-500">일</span>
              </div>
            </div>
            {(errors.year1 || errors.month1 || errors.day1) && (
              <p className={errorClass}>{errors.year1 || errors.month1 || errors.day1}</p>
            )}
          </div>

          {/* 3행: 출생시간 + 시간모름 */}
          <div>
            <div className="!flex !items-center !gap-4">
              <div>
                <label className={labelClass}>출생시간</label>
                <div className="!flex !gap-2 !items-center">
                  <div className="!flex !items-center !gap-1">
                    <Input
                      textAlign="left"
                      value={formData.hour1}
                      onChange={(e) => handleChange('hour1', e.target.value)}
                      placeholder="14"
                      type="number"
                      width={60}
                      disabled={unknownTime1}
                    />
                    <span className={`!text-gray-500 ${unknownTime1 ? '!opacity-50' : ''}`}>시</span>
                  </div>
                  <div className="!flex !items-center !gap-1">
                    <Input
                      textAlign="left"
                      value={formData.minute1}
                      onChange={(e) => handleChange('minute1', e.target.value)}
                      placeholder="30"
                      type="number"
                      width={60}
                      disabled={unknownTime1}
                    />
                    <span className={`!text-gray-500 ${unknownTime1 ? '!opacity-50' : ''}`}>분</span>
                  </div>
                </div>
              </div>
              <label className="!flex !items-center !gap-1 !text-sm !text-gray-600 !cursor-pointer !mt-6">
                <input
                  type="checkbox"
                  checked={unknownTime1}
                  onChange={(e) => {
                    setUnknownTime1(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, hour1: '', minute1: '' }));
                    }
                  }}
                  className="!w-4 !h-4 !rounded !border-gray-300"
                />
                시간모름
              </label>
            </div>
            {(errors.hour1 || errors.minute1) && (
              <p className={errorClass}>{errors.hour1 || errors.minute1}</p>
            )}
          </div>
        </div>

        {/* 고객 정보 2 (궁합 상품일 때만) */}
        {selectedProduct && selectedProduct.custInfoCount >= 2 && (
          <div className="!bg-white !rounded-lg !shadow-sm !p-6 !mb-6">
            <h2 className="!text-lg !font-semibold !text-gray-800 !mb-4 !pb-2 !border-b">
              고객 정보 2 <span className="!text-red-500">*</span>
              <span className="!text-sm !font-normal !text-gray-500 !ml-2">(궁합 상품)</span>
            </h2>
            
            {/* 1행: 이름, 성별, 양/음력 */}
            <div className="!grid !grid-cols-1 sm:!grid-cols-3 !gap-4 !mb-4">
              <div>
                <label className={labelClass}>이름 <span className="!text-red-500">*</span></label>
                <Input
                  textAlign="left"
                  value={formData.name2}
                  onChange={(e) => handleChange('name2', e.target.value)}
                  placeholder="김철수"
                  width="100%"
                />
                {errors.name2 && <p className={errorClass}>{errors.name2}</p>}
              </div>
              <div>
                <label className={labelClass}>성별 <span className="!text-red-500">*</span></label>
                <Select
                  options={[
                    { value: '', option: '선택하세요' },
                    { value: '남성', option: '남성' },
                    { value: '여성', option: '여성' },
                  ]}
                  value={formData.sex2}
                  onChange={(e) => handleChange('sex2', e.target.value)}
                  className="!w-full"
                />
                {errors.sex2 && <p className={errorClass}>{errors.sex2}</p>}
              </div>
              <div>
                <label className={labelClass}>양/음력 <span className="!text-red-500">*</span></label>
                <Select
                  options={[
                    { value: '', option: '선택하세요' },
                    { value: '양력', option: '양력' },
                    { value: '음력', option: '음력' },
                    { value: '음력(윤달)', option: '음력(윤달)' },
                  ]}
                  value={formData.solarType2}
                  onChange={(e) => handleChange('solarType2', e.target.value)}
                  className="!w-full"
                />
                {errors.solarType2 && <p className={errorClass}>{errors.solarType2}</p>}
              </div>
            </div>

            {/* 2행: 생년월일 */}
            <div className="!mb-4">
              <label className={labelClass}>생년월일 <span className="!text-red-500">*</span></label>
              <div className="!flex !flex-wrap !gap-2 !items-center">
                <div className="!flex !items-center !gap-1">
                  <Input
                    textAlign="left"
                    value={formData.year2}
                    onChange={(e) => handleChange('year2', e.target.value)}
                    placeholder="1992"
                    type="number"
                    width={80}
                  />
                  <span className="!text-gray-500">년</span>
                </div>
                <div className="!flex !items-center !gap-1">
                  <Input
                    textAlign="left"
                    value={formData.month2}
                    onChange={(e) => handleChange('month2', e.target.value)}
                    placeholder="3"
                    type="number"
                    width={60}
                  />
                  <span className="!text-gray-500">월</span>
                </div>
                <div className="!flex !items-center !gap-1">
                  <Input
                    textAlign="left"
                    value={formData.day2}
                    onChange={(e) => handleChange('day2', e.target.value)}
                    placeholder="20"
                    type="number"
                    width={60}
                  />
                  <span className="!text-gray-500">일</span>
                </div>
              </div>
              {(errors.year2 || errors.month2 || errors.day2) && (
                <p className={errorClass}>{errors.year2 || errors.month2 || errors.day2}</p>
              )}
            </div>

            {/* 3행: 출생시간 + 시간모름 */}
            <div>
              <div className="!flex !items-center !gap-4">
                <div>
                  <label className={labelClass}>출생시간</label>
                  <div className="!flex !gap-2 !items-center">
                    <div className="!flex !items-center !gap-1">
                      <Input
                        textAlign="left"
                        value={formData.hour2}
                        onChange={(e) => handleChange('hour2', e.target.value)}
                        placeholder="9"
                        type="number"
                        width={60}
                        disabled={unknownTime2}
                      />
                      <span className={`!text-gray-500 ${unknownTime2 ? '!opacity-50' : ''}`}>시</span>
                    </div>
                    <div className="!flex !items-center !gap-1">
                      <Input
                        textAlign="left"
                        value={formData.minute2}
                        onChange={(e) => handleChange('minute2', e.target.value)}
                        placeholder="0"
                        type="number"
                        width={60}
                        disabled={unknownTime2}
                      />
                      <span className={`!text-gray-500 ${unknownTime2 ? '!opacity-50' : ''}`}>분</span>
                    </div>
                  </div>
                </div>
                <label className="!flex !items-center !gap-1 !text-sm !text-gray-600 !cursor-pointer !mt-6">
                  <input
                    type="checkbox"
                    checked={unknownTime2}
                    onChange={(e) => {
                      setUnknownTime2(e.target.checked);
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, hour2: '', minute2: '' }));
                      }
                    }}
                    className="!w-4 !h-4 !rounded !border-gray-300"
                  />
                  시간모름
                </label>
              </div>
              {(errors.hour2 || errors.minute2) && (
                <p className={errorClass}>{errors.hour2 || errors.minute2}</p>
              )}
            </div>
          </div>
        )}

        {/* 버튼 영역 */}
        <div className="!flex !flex-col sm:!flex-row !justify-end !gap-3">
          <button
            type="button"
            onClick={() => handleSubmit('N')}
            className="!px-6 !py-3 !text-sm !font-medium !text-white !bg-blue-600 !rounded-lg hover:!bg-blue-700 !transition-colors disabled:!bg-gray-400"
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faPlus} className="!mr-2" />
            {isSubmitting ? '등록 중...' : '주문 등록'}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit('Y')}
            className="!px-6 !py-3 !text-sm !font-medium !text-white !bg-green-600 !rounded-lg hover:!bg-green-700 !transition-colors disabled:!bg-gray-400"
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faPlus} className="!mr-2" />
            {isSubmitting ? '등록 중...' : '실시간 등록'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSingle;
